import { useMemo } from "react";
import type { Category, PlanEvent, Task } from "../../../shared/types";

export function useEventIndexing(
  events: PlanEvent[],
  tasks: Task[],
  selectedDate: string | null,
) {
  const eventsByDate = useMemo(() => {
    const map = new Map<string, PlanEvent[]>();
    for (const e of events) {
      const existing = map.get(e.date) ?? [];
      existing.push(e);
      map.set(e.date, existing);
    }
    return map;
  }, [events]);

  const tasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      if (t.dueDate) {
        const existing = map.get(t.dueDate) ?? [];
        existing.push(t);
        map.set(t.dueDate, existing);
      }
    }
    return map;
  }, [tasks]);

  const unlinkedTasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      if (t.dueDate && !t.eventId && !t.archived) {
        const existing = map.get(t.dueDate) ?? [];
        existing.push(t);
        map.set(t.dueDate, existing);
      }
    }
    return map;
  }, [tasks]);

  const categoryDotsForDate = useMemo(() => {
    const map = new Map<string, Set<Category>>();
    for (const [date, evts] of eventsByDate) {
      const cats = new Set<Category>();
      for (const e of evts) cats.add(e.category);
      map.set(date, cats);
    }
    for (const [date, tks] of tasksByDate) {
      const cats = map.get(date) ?? new Set<Category>();
      for (const t of tks) cats.add(t.category);
      map.set(date, cats);
    }
    return map;
  }, [eventsByDate, tasksByDate]);

  const selectedEvents = selectedDate
    ? (eventsByDate.get(selectedDate) ?? [])
    : [];
  const selectedTasks = selectedDate
    ? (tasksByDate.get(selectedDate) ?? [])
    : [];

  return {
    eventsByDate,
    tasksByDate,
    unlinkedTasksByDate,
    categoryDotsForDate,
    selectedEvents,
    selectedTasks,
  };
}
